// Usage examples:
{
  // id different than overlay-name, both need to be unique. prevents page jump on open
  // id should be used for targets
  /* <overlay-component id="myOverlay" overlay-name="overlay-left" buttons-outside-context="true">
  <div class="overlay-container" data-overlay-container>
    <div class="overlay-panel" data-animation-direction="left">
      {% render 'overlay-header' %}
      <h2 class="overlay-title">Left Slide-In</h2>
      <div class="overlay-body" data-overlay-content>
        <p>This overlay slides in from the left side of the screen.</p>
      </div>

      </div>
    </div>
      
    <button class="overlay-trigger" data-target="myOverlay">Open Left Overlay</button>
</overlay-component>

<overlay-component id="myOverlay2" overlay-name="overlay-right" buttons-outside-context="false">
  <div class="overlay-container" data-overlay-container="myOverlay2">
    <div class="overlay-panel" data-animation-direction="right">
      {% render 'overlay-header' %}
      <h2 class="overlay-title">Right Slide-In</h2>
      <div class="overlay-body" data-overlay-content>
        <p>This overlay slides in from the right side of the screen.</p>
      </div>

      </div>
    </div>
    <button class="overlay-trigger" data-target="myOverlay2">Open Right Overlay</button>
</overlay-component>

<overlay-component id="myOverlay3" overlay-name="overlay-center" buttons-outside-context="true">
  <div class="overlay-container" data-overlay-container="myOverlay3">
    <div class="overlay-panel" data-animation-direction="center">
      {% render 'overlay-header' %}
      <h2 class="overlay-title">Center Fade-In</h2>
      <div class="overlay-body" data-overlay-content>
        <p>This overlay fades in from the center of the screen.</p>
      </div>
      </div>
    </div>

    <button class="overlay-trigger" data-target="myOverlay3">Open Center Overlay</button>
</overlay-component> */
}

class OverlayComponent extends HTMLElement {
  #initialized = false;

  #container = null;
  #inner = null;
  #closeButtons = [];
  #triggerButtons = [];

  #name = 'overlay';
  #buttonsOutsideContext = false;
  #allOverlaysWithTheSameId = [];

  #shouldTrapFocus = false;
  #shouldReturnFocus = false;
  #shouldFocusWithin = true;
  #returnFocusTo = null;

  #openedBy = null;
  #state = {
    open: false,
  };
  get state() {
    return this.#state;
  }

  constructor() {
    super();
  }

  connectedCallback() {
    if (window.Shopify && window.Shopify.designMode) {
      this.#init();
      return;
    }
    if (document.readyState === 'complete') {
      this.#init();
      return;
    }

    this.#initBound = this.#init.bind(this);
    window.addEventListener('load', this.#initBound);
  }

  #initBound = null;
  #init() {
    this.#container =
      document.querySelector(`[data-overlay-container="${this.id}"]`) ||
      this.querySelector('[data-overlay-container]') ||
      this.querySelector('.overlay-container');
    this.#inner =
      this.#container?.querySelector(`[data-overlay-container]`) ||
      this.#container?.querySelector('.overlay-container');
    this.#closeButtons = this.querySelectorAll('.overlay-close');
    this.#triggerButtons = this.querySelectorAll(`.overlay-trigger[data-target="${this.id}"]`);
    this.#name = this.getAttribute('overlay-name') || 'overlay';
    this.#buttonsOutsideContext = this.getAttribute('buttons-outside-context') || 'false';

    this.#shouldFocusWithin = this.getAttribute('focus-within') !== 'false';
    this.#shouldTrapFocus = this.hasAttribute('trap-focus') && this.getAttribute('trap-focus') !== 'false';
    this.#shouldReturnFocus = this.hasAttribute('return-focus');
    this.#returnFocusTo = this.getAttribute('return-focus')
      ? document.querySelector(this.getAttribute('return-focus'))
      : null;

    if (this.#buttonsOutsideContext == 'true') {
      this.#triggerButtons = document.querySelectorAll(`.overlay-trigger[data-target="${this.id}"]`);
    } else {
      this.#triggerButtons = this.querySelectorAll(`.overlay-trigger[data-target="${this.id}"]`);
    }

    if (!this.#container) return;

    this.#moveOverlayToBody();
    this.#labelDialog();
    this.#addEventListeners();
    this.#syncStateAndHash();
  }

  disconnectedCallback() {
    this.#removeEventListeners();
  }

  #addEventListeners() {
    this.#triggerOpenBound = this.#triggerOpen.bind(this);
    this.#onHashChangeBound = this.#onHashChange.bind(this);
    this.#handleOverlayClickBound = this.#handleOverlayClick.bind(this);
    this.#handleKeyUpBound = this.#handleKeyUp.bind(this);
    this.#closeBound = this.close.bind(this);
    this.#container.addEventListener('click', this.#handleOverlayClickBound);
    if (this.#closeButtons?.length) {
      this.#closeButtons.forEach((button) => {
        button.addEventListener('click', this.#closeBound);
      });
    }
    this.#triggerButtons.forEach((button) => {
      button.addEventListener('click', this.#triggerOpenBound);
    });
    window.addEventListener('keyup', this.#handleKeyUpBound);
    window.addEventListener('hashchange', this.#onHashChangeBound);
  }

  #removeEventListeners() {
    this.#container.removeEventListener('click', this.#handleOverlayClickBound);
    if (this.#closeButtons?.length) {
      this.#closeButtons.forEach((button) => {
        button.removeEventListener('click', this.#closeBound);
      });
    }
    this.#triggerButtons.forEach((button) => {
      button.removeEventListener('click', this.#triggerOpenBound);
    });
    window.removeEventListener('keyup', this.#handleKeyUpBound);
    window.removeEventListener('hashchange', this.#onHashChangeBound);
  }

  #updatingHash = false;

  #onHashChangeBound = null;
  #onHashChange(e) {
    const changed = this.#syncStateAndHash();
    if (changed) {
      e.preventDefault();
    }
  }
  #syncStateAndHash() {
    if (this.#updatingHash) {
      this.#updatingHash = false;
      return false;
    }

    const hash = window.location.hash.substring(1);
    if (hash === this.#name) {
      if (this.classList.contains('is-open')) return false;
      this.open(false);
      return true;
    }

    return false;
  }

  #handleOverlayClickBound = null;
  #handleOverlayClick(event) {
    if (event.target === this.#container) {
      this.close();
    }
  }

  #handleKeyUpBound = null;
  #handleKeyUp(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  #triggerOpenBound = null;
  #triggerOpen(e) {
    const index = e.currentTarget.dataset.index !== undefined ? Number(e.currentTarget.dataset.index) : null;
    this.open(true, index);
    if (e.currentTarget.hasAttribute('aria-expanded')) {
      e.currentTarget.setAttribute('aria-expanded', 'true');
    }
    this.#openedBy = e.currentTarget;
  }

  open(updateHash = true, index = null) {
    this.#openedBy = null;

    if (!this.#container || this.#container.classList.contains('is-open')) return;
    this.#state.open = true;

    this.#container.classList.add('is-open');
    if (updateHash) {
      this.#updatingHash = true;
      location.replace(`#${this.#name}`);
    }

    if (this.#shouldTrapFocus) {
      window.trapFocus(this.#container);
    }
    if (this.#shouldFocusWithin) {
      this.#focusWithin();
    }

    document.body.style.overflow = 'hidden';
    this.#animateSlideIn();

    if (index !== null) {
      requestAnimationFrame(() => {
        const slider = this.#container?.querySelector('.slider');
        const firstSlide = slider?.querySelector('.slideshow__slide');
        if (slider && firstSlide) {
          slider.scrollTo({ left: firstSlide.clientWidth * index, behavior: 'instant' });
        }
      });
    }

    this.dispatchEvent(new CustomEvent('overlay-component:open', { detail: { index } }));
  }

  #closeBound = null;
  close({ returnFocus = true } = {}) {
    this.#animateSlideOut().then(() => {
      this.#container.classList.remove('is-open');

      // Close inner overlay if present
      this.#inner?.classList?.remove('is-open');

      if (window.location.hash === `#${this.#name}` && history.replaceState) {
        history.replaceState(null, null, window.location.pathname + window.location.search);
      }
      // Always dispatch hashchange so body-controller can update inert state
      // (hash may have been removed by pushState from facet filters)
      window.dispatchEvent(new HashChangeEvent('hashchange'));
      document.body.style.overflow = 'auto';
      if (this.#shouldTrapFocus) {
        window.removeTrapFocus(this.#container);
      }
      if (returnFocus && this.#shouldReturnFocus && (this.#openedBy || this.#returnFocusTo)) {
        this.#returnFocus();
      }

      this.#triggerButtons.forEach((button) => {
        if (button.hasAttribute('aria-expanded')) {
          button.setAttribute('aria-expanded', 'false');
        }
      });

      this.#state.open = false;

      this.dispatchEvent(new CustomEvent('overlay-component:close'));
    });
  }

  // Every panel was a bare <div>. Focus moves to the first control on open, so a
  // screen reader announced that control and never what opened. Name the panel
  // from its overlay-header title. Cited in Gazonni v. AKRIS, para 42(e).
  #labelDialog() {
    const panel = this.#container.querySelector(':scope > .overlay-panel') || this.#container;
    if (!panel.hasAttribute('role')) panel.setAttribute('role', 'dialog');
    if (!panel.hasAttribute('aria-modal')) panel.setAttribute('aria-modal', 'true');
    if (panel.hasAttribute('aria-label') || panel.hasAttribute('aria-labelledby')) return;

    const title = this.#ownTitle(panel);
    if (!title) return;
    if (!title.id) title.id = `${this.id || this.#name}-title`;
    panel.setAttribute('aria-labelledby', title.id);
  }

  // The panel's own, non-empty title. A nested inner overlay carries its own
  // .overlay-title (lookbook: "Shop the look"); a header rendered without a
  // title is an empty <h3> (gallery zoom). Neither names or receives focus.
  #ownTitle(panel) {
    return Array.from(panel.querySelectorAll('.overlay-title')).find(
      (el) => el.closest('.overlay-panel') === panel && el.textContent.trim()
    );
  }

  #focusWithin() {
    // A named dialog announces itself when it takes focus. Focusing the first
    // control instead made VoiceOver read "close, button", and when that control
    // was the size panel's hidden back button, focus() was a no-op and never
    // entered the panel. Cited in Gazonni v. AKRIS, para 42(e), 42(g).
    const panel = this.#container.querySelector(':scope > .overlay-panel') || this.#container;
    const isNamedDialog =
      panel.getAttribute('role') === 'dialog' &&
      (panel.hasAttribute('aria-label') || panel.hasAttribute('aria-labelledby'));
    if (isNamedDialog) {
      // Prefer the dialog's heading: VoiceOver reads a focused bare panel as
      // "selectable text" and only names the dialog on the next Tab; a focused
      // heading is announced as "<title>, heading" inside the named dialog.
      const target = this.#ownTitle(panel) || panel;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      // trapFocus only wraps from its first/last control. The heading is
      // neither, so Shift+Tab from it left the dialog when the page behind was
      // not inert (listing pages open the size panel without a hash).
      if (this.#shouldTrapFocus && target !== panel && !target.dataset.wrapsShiftTab) {
        target.dataset.wrapsShiftTab = '';
        target.addEventListener('keydown', (event) => {
          if (event.key !== 'Tab' || !event.shiftKey) return;
          const last = window
            .getFocusableElements(this.#container)
            .filter((el) => !el.hidden && el.checkVisibility?.() !== false)
            .at(-1);
          if (!last) return;
          event.preventDefault();
          last.focus();
        });
      }
      target.focus({ preventScroll: true });
      return;
    }

    const firstFocusable = window
      .getFocusableElements(this.#container)
      ?.find((el) => !el.hidden && el.checkVisibility?.() !== false);

    if (firstFocusable) {
      firstFocusable.focus({ preventScroll: true });
    } else {
      this.#container.setAttribute('tabindex', '-1');
      this.#container.focus({ preventScroll: true });
      this.#container.removeAttribute('tabindex');
    }
  }

  #returnFocus() {
    const returnTarget = this.#returnFocusTo || this.#openedBy;

    if (!returnTarget || !returnTarget.checkVisibility()) {
      return;
    }

    returnTarget.focus({ preventScroll: true });
  }

  #moveOverlayToBody() {
    if (this.#initialized) return;

    this.#allOverlaysWithTheSameId = document.querySelectorAll(`[data-overlay-container="${this.id}"]`);
    const hasMoreThanOneOverlay = this.#allOverlaysWithTheSameId.length > 1;

    if (hasMoreThanOneOverlay) {
      this.#allOverlaysWithTheSameId.forEach((overlay, index) => {
        if (index !== 0 && overlay.parentNode.contains(overlay)) {
          overlay.parentNode.removeChild(overlay);
        }
      });
    }

    if (this.contains(this.#container)) {
      this.removeChild(this.#container);
    }

    document.body.appendChild(this.#container);
    this.#initialized = true;
  }

  #getEasingFromCSSVariable() {
    const rootStyle = getComputedStyle(document.documentElement);
    return rootStyle.getPropertyValue('--ease-smooth').trim() || 'ease';
  }

  #animateSlideIn() {
    const panel =
      this.#container.querySelector('[data-overlay-panel]') || this.#container.querySelector('.overlay-panel');
    const direction = panel.dataset.animationDirection;
    const easing = this.#getEasingFromCSSVariable();

    if (direction === 'left' || direction === 'right') {
      const offset = direction === 'left' ? -100 : 100;
      panel.animate([{ transform: `translateX(${offset}%)` }, { transform: 'translateX(0)' }], {
        duration: 600,
        easing: easing,
        fill: 'forwards',
      });
    } else if (direction === 'center') {
      panel.style.opacity = 0;
      panel.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 700,
        easing: easing,
        fill: 'forwards',
      });
    }
  }

  #animateSlideOut() {
    return new Promise((resolve) => {
      const panel =
        this.#container.querySelector('[data-overlay-panel]') || this.#container.querySelector('.overlay-panel');
      const direction = panel.dataset.animationDirection;
      const easing = this.#getEasingFromCSSVariable(); // Retrieve easing from CSS variable

      if (direction === 'left' || direction === 'right') {
        const offset = direction === 'left' ? -100 : 100;
        const animation = panel.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${offset}%)` }], {
          duration: 300,
          easing: easing,
          fill: 'forwards',
        });
        animation.onfinish = resolve;
      } else if (direction === 'center') {
        const animation = panel.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 300,
          easing: easing,
          fill: 'forwards',
        });

        animation.onfinish = resolve;
      }
    });
  }
}

customElements.define('overlay-component', OverlayComponent);
